import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { X, Sparkles, Check } from "lucide-react";

const LS_KEY = "ambdrop_scent_saved_v1";
const SESSION_DISMISS = "ambdrop_scent_dismiss_session";
const IDLE_MS = 25000; // show after 25s on page

export const AbandonCatcher = ({ productId, productName }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef(null);
  const shownRef = useRef(false);

  useEffect(() => {
    // Never show twice — respect long-term save + session dismiss
    if (localStorage.getItem(LS_KEY)) return;
    if (sessionStorage.getItem(SESSION_DISMISS)) return;

    const trigger = () => {
      if (shownRef.current) return;
      if (localStorage.getItem(LS_KEY)) return;
      if (sessionStorage.getItem(SESSION_DISMISS)) return;
      shownRef.current = true;
      setOpen(true);
    };

    // 1. idle timer
    timerRef.current = setTimeout(trigger, IDLE_MS);

    // 2. desktop exit intent — mouse leaving top of viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearTimeout(timerRef.current);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const close = () => {
    setOpen(false);
    // don't nag again this session
    sessionStorage.setItem(SESSION_DISMISS, "1");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads/save-scent", {
        email: email.trim(),
        product_id: productId,
        source: "abandon_slidein",
      });
      localStorage.setItem(LS_KEY, "1");
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="abandon-catcher"
      role="dialog"
      className="fixed z-[70] bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 max-w-sm"
    >
      <div className="relative bg-[#0F0C0A] border border-[#D4AF37] shadow-2xl p-6 sm:p-7 overflow-hidden">
        {/* Subtle gold glow */}
        <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#D4AF37]/20 blur-3xl" />
        <button
          data-testid="abandon-close"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 text-[#B0A496] hover:text-[#F4EAD9]"
        >
          <X size={16} strokeWidth={1.2} />
        </button>

        {!submitted ? (
          <>
            <div className="flex items-center gap-2 text-[#D4AF37] mb-4">
              <Sparkles size={14} strokeWidth={1.2} />
              <p className="text-[10px] tracking-overline uppercase font-bold">
                Before you drift away
              </p>
            </div>
            <h3 className="font-serif-display text-2xl leading-tight text-[#F4EAD9]">
              Save {productName ? <em className="italic text-[#D4AF37]">{productName}</em> : "this scent"} for later.
            </h3>
            <p className="mt-3 text-sm text-[#B0A496] leading-relaxed">
              We&apos;ll email it to you with a private <span className="text-[#D4AF37] font-bold">10% welcome code</span> — no spam, no drama.
            </p>
            <form data-testid="abandon-form" onSubmit={submit} className="mt-5 space-y-3">
              <input
                data-testid="abandon-email-input"
                type="email"
                required
                placeholder="you@somewhere.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border border-[#2D2622] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4EAD9] placeholder:text-[#5C4F4A] transition-colors"
              />
              {error && (
                <p data-testid="abandon-error" className="text-xs text-[#E36B6B]">
                  {error}
                </p>
              )}
              <button
                data-testid="abandon-submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase font-bold transition-colors disabled:opacity-70"
              >
                {loading ? "Saving…" : "Send my code"}
              </button>
              <button
                type="button"
                onClick={close}
                data-testid="abandon-dismiss"
                className="w-full text-[10px] tracking-overline uppercase font-bold text-[#5C4F4A] hover:text-[#B0A496]"
              >
                No thanks
              </button>
            </form>
          </>
        ) : (
          <div data-testid="abandon-success">
            <div className="flex items-center gap-2 text-[#D4AF37] mb-4">
              <Check size={16} strokeWidth={1.2} />
              <p className="text-[10px] tracking-overline uppercase font-bold">Saved</p>
            </div>
            <h3 className="font-serif-display text-2xl leading-tight text-[#F4EAD9]">
              Check your inbox.
            </h3>
            <p className="mt-3 text-sm text-[#B0A496]">
              Your welcome code is <span className="text-[#D4AF37] font-bold tracking-wider">AMBDROP10</span> — good for 10% off your first bottle.
            </p>
            <button
              onClick={close}
              data-testid="abandon-close-success"
              className="mt-6 text-[11px] tracking-overline uppercase font-bold text-[#D4AF37] hover:text-[#F4EAD9]"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AbandonCatcher;
